import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import { Button } from '@/components/ui/Button'
import PlanHeader from '../components/PlanHeader'
import SubmitMessageCard from '../TopDown/components/SubmitMessageCard'
import ReviewersCard from '../TopDown/components/ReviewersCard'
import ReviewTaskCard from '../TopDown/components/ReviewTaskCard'
import PricingTable from './components/PricingTable'
import { pricingSummaryText, type PricingDiff } from './components/pricingDiff'
import type { ChannelRevisionSubmitData } from '../components/ChannelRevisionSubmitWidget'
import type { PricingPageProps } from './pricingTypes'
import { type Person, type Task } from '@/pages/Tasks/task'
import { plan_product_pricings_edit_path, plan_product_pricings_index_path, submissions_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

interface Props extends PricingPageProps {
  // The channel's draft against the published pricings.
  pricing_diff: PricingDiff
  // The open pricing task (the channel's cycle task, or a resubmit): its card
  // carries the reviewers.
  submit_task: Task | null
  people: Person[]
  // A revision of published pricings has no task yet: reviewers are picked here.
  pricing_revision_submit: ChannelRevisionSubmitData | null
}

// The doer's submit page for one channel's pricings, GitHub's "Open a pull
// request", same as Plan/TopDown/Submit: the message on the left, who
// reviews on the right (the task's card, or a reviewer picker for a
// revision), and the changes against the published pricings below. Posting
// goes through the same submit verb (SubmissionsController#create).
export default function ProductPricingsSubmit({ plan, price_levels, channel, groups, pricing_diff, submit_task, people, pricing_revision_submit }: Props) {
  const market = useMarket()
  const [message, setMessage] = useState('')
  const [reviewerIds, setReviewerIds] = useState<string[]>(pricing_revision_submit?.default_reviewer_ids ?? [])
  const [changesOnly, setChangesOnly] = useState(true)
  const hasReviewers = submit_task ? submit_task.reviewers.length > 0 : reviewerIds.length > 0
  const currency = plan.currency ?? market.currency
  const channelSlug = channel?.slug ?? ''

  const hint = hasReviewers
    ? 'Published pricings stay as they are until a reviewer approves.'
    : submit_task ? 'No reviewer on this task — this will publish the pricings right away.' : null
  const submitLabel = hasReviewers ? 'Submit for review' : submit_task ? 'Mark completed' : 'Publish changes'

  const submit = () => {
    const back_to = plan_product_pricings_index_path(market.slug, { channel: channelSlug })
    if (submit_task) {
      router.post(submissions_path(), { task_id: submit_task.id, message, back_to })
    } else if (pricing_revision_submit) {
      router.post(submissions_path(), {
        plan_id: pricing_revision_submit.plan_id,
        channel_id: pricing_revision_submit.channel_id,
        reviewer_ids: reviewerIds,
        message,
        back_to,
      })
    }
  }

  return (
    <AppLayout
      header={<PlanHeader
        name={plan.name}
        orgNodeName={plan.org_node?.name ?? null}
        revenueTarget={plan.revenue_target}
        currency={plan.currency}
        beginDate={plan.begin_date}
        endDate={plan.end_date}
      />}
    >
      <Head title={`${channel?.name ?? plan.name} pricings — Submit`} />
      <div className="flex items-start gap-4">
        <div className="min-w-0 lg:w-3/5">
          <SubmitMessageCard
            label={hasReviewers ? 'Message to your reviewer' : 'Message'}
            placeholder="What changed and why"
            hint={hint}
            warning={!hasReviewers}
            message={message}
            onChange={setMessage}
            submitLabel={submitLabel}
            onSubmit={submit}
            onCancel={() => router.visit(plan_product_pricings_edit_path(market.slug, plan.slug, { channel: channelSlug }))}
          />
        </div>
        {submit_task
          ? <ReviewTaskCard task={submit_task} people={people} />
          : pricing_revision_submit && (
            <ReviewersCard people={pricing_revision_submit.people} selectedIds={reviewerIds} onChange={setReviewerIds} />
          )}
      </div>
      <div
        className="mt-6 mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground"
        style={{ background: 'var(--info-surface)', borderColor: 'var(--border)' }}
      >
        <span className="font-semibold">{channel?.name} · Products &amp; Pricings.</span>{' '}
        {pricingSummaryText(pricing_diff.summary)}.
        {pricing_diff.summary.any_changes && ' Green is what this submission adds and red is the published value it replaces.'}
        {pricing_diff.levels.removed.length > 0 && (
          <> Price levels removed: {pricing_diff.levels.removed.map((l) => l.name).join(', ')}.</>
        )}
      </div>
      <div className="overflow-hidden rounded-md border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-2 text-[12px] text-muted-foreground">
          <span>{channel?.name}</span>
          <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => setChangesOnly((v) => !v)}>
            {changesOnly ? 'Show all rows' : 'Show changes only'}
          </Button>
        </div>
        <PricingTable
          groups={groups}
          priceLevels={price_levels}
          canEdit={false}
          emptyMessage={changesOnly ? 'No changed rows.' : `No products for ${channel?.name ?? 'this channel'}.`}
          diff={pricing_diff}
          changesOnly={changesOnly}
          currency={currency}
        />
      </div>
    </AppLayout>
  )
}
